/**
 * Payment Service v2.0 - Service-Based Model with Error Handling
 * Integrates with StratIQX Payment Worker v2.0 (payments.stratiqx.ai)
 * Implements comprehensive error handling and request tracking
 */

import { ErrorHandler, PaymentError, ServiceError, NetworkError } from '../utils/errors';
import { ServiceConfigUtils } from '../config/services.utils';

// Service-based interfaces (replacing plan-based)
export interface ServiceDetails {
  id: string;
  name: string;
  description: string;
  price: {
    value: number;
    display: string;
  };
  deposit: {
    type: 'fixed' | 'percentage' | 'full-price' | 'none';
    value?: number;
    amount: number;
    display: string;
  };
  billingType: 'one-time' | 'monthly';
  features: string[];
  deliveryTime: string;
}

export interface ServicePurchase {
  id: number;
  serviceId: string;
  serviceName: string;
  description: string;
  amount: number;
  depositPaid: number;
  balanceDue: number;
  status: 'pending' | 'active' | 'in_progress' | 'completed' | 'cancelled' | 'failed';
  billingType: string;
  stripePaymentId: string;
  activatedAt: string | null;
  createdAt: string;
  metadata: Record<string, any>;
}

export interface PaymentIntent {
  success: boolean;
  clientSecret: string;
  paymentIntentId: string;
  amount: number;
  originalAmount: number;
  serviceDetails: {
    id: string;
    name: string;
    deposit: {
      type: 'fixed' | 'percentage' | 'full-price' | 'none';
      value?: number;
      amount: number;
      display: string;
    };
    billingType: string;
  };
  appliedDiscount?: {
    valid: boolean;
    type: string;
    value: number;
  } | null;
  currency: string;
}

export interface UserServices {
  success: boolean;
  userId: string;
  activeServices: ServicePurchase[];
  paymentHistory: PaymentRecord[];
  deliveries: ServiceDelivery[];
  statistics: UserStatistics;
  hasActiveServices: boolean;
  timestamp: string;
}

export interface PaymentRecord {
  id: number;
  stripePaymentId: string;
  amount: number;
  status: 'succeeded' | 'failed' | 'pending' | 'cancelled';
  serviceType: string;
  error?: {
    code: string;
    message: string;
  };
  createdAt: string;
}

export interface ServiceDelivery {
  id: number;
  type: string;
  status: string;
  url?: string;
  deliveredAt: string;
  serviceId: string;
  serviceName: string;
}

export interface UserStatistics {
  totalActiveServices: number;
  totalPayments: number;
  totalSpent: number;
  successfulPayments: number;
  failedPayments: number;
  lastPaymentDate: string | null;
  averagePaymentAmount: number;
}

export interface PaymentRequest {
  amount: number;
  serviceType: string; // Changed from planType
  userId: string;
  promoCode?: string;
}

export interface PromoValidation {
  valid: boolean;
  discount?: {
    type: 'amount' | 'percent';
    value: number;
  };
  message?: string;
}

export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    category?: string;
    details?: Record<string, any>;
  };
  timestamp: string;
}

/**
 * Main Payment Service Class
 */
class PaymentServiceV2 {
  private baseURL: string;
  private accessToken: string | null = null;
  private requestTracker: Map<string, number> = new Map();

  constructor() {
    // FORCE use of service binding - override any environment variables
    const envVar = import.meta.env.VITE_PAYMENT_API_URL;
    this.baseURL = '/api/payment'; // HARDCODED to force service bindings
    this.accessToken = localStorage.getItem('stratiqx_access_token');
    
    console.log('🚨 FORCED SERVICE BINDING MODE - baseURL hardcoded to /api/payment');
    console.log('🚨 Environment variable was:', envVar);
    
    // Log service initialization
    console.log('🔧 PaymentService v2.0 initialized', {
      baseURL: this.baseURL,
      hasToken: !!this.accessToken
    });
    console.log('🎯 PAYMENT SERVICE BASE URL:', this.baseURL);
    console.log('🎯 VITE_PAYMENT_API_URL env var:', import.meta.env.VITE_PAYMENT_API_URL);
  }

  /**
   * Enhanced request method with error handling and tracking
   */
  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const requestId = this.generateRequestId();
    const url = `${this.baseURL}${endpoint}`;
    const startTime = Date.now();
    
    // Track request
    this.requestTracker.set(requestId, startTime);
    
    try {
      console.log(`🚀 [${requestId}] ${options.method || 'GET'} ${endpoint}`);
      
      const defaultHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Request-ID': requestId,
        'X-Client-Version': '2.0.0',
      };

      if (this.accessToken) {
        defaultHeaders['Authorization'] = `Bearer ${this.accessToken}`;
      }

      const config: RequestInit = {
        ...options,
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
      };

      const response = await fetch(url, config);
      const duration = Date.now() - startTime;
      
      // Get response headers for debugging
      const responseHeaders = {
        requestId: response.headers.get('X-Request-ID'),
        errorCode: response.headers.get('X-Error-Code'),
        responseTime: response.headers.get('X-Response-Time'),
        serviceVersion: response.headers.get('X-Service-Version'),
        cache: response.headers.get('X-Cache')
      };

      console.log(`📊 [${requestId}] Response: ${response.status} (${duration}ms)`, responseHeaders);

      // Handle different response types
      if (!response.ok) {
        await this.handleErrorResponse(response, requestId, endpoint);
      }

      const contentType = response.headers.get('content-type');
      let data: T;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = (await response.text()) as any;
      }

      // Validate response structure for API responses
      if (typeof data === 'object' && data !== null) {
        this.validateResponseStructure(data, endpoint);
      }

      console.log(`✅ [${requestId}] Success: ${endpoint} (${duration}ms)`);
      return data;

    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(`❌ [${requestId}] Error: ${endpoint} (${duration}ms)`, error);
      
      // Handle different error types
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new NetworkError(
          'NETWORK_ERROR',
          'Network connection failed. Please check your internet connection.',
          { endpoint, requestId, duration }
        );
      }

      if (error instanceof PaymentError || error instanceof ServiceError) {
        throw error;
      }

      throw new PaymentError(
        'REQUEST_FAILED',
        'Request failed unexpectedly',
        { 
          originalError: error.message,
          endpoint,
          requestId,
          duration
        }
      );
    } finally {
      // Clean up tracking
      this.requestTracker.delete(requestId);
    }
  }

  /**
   * Handle error responses with proper error mapping
   */
  private async handleErrorResponse(response: Response, requestId: string, endpoint: string): Promise<never> {
    let errorData: ApiError;
    
    try {
      errorData = await response.json();
    } catch (e) {
      // If we can't parse the error response, create a generic one
      errorData = {
        success: false,
        error: {
          code: 'HTTP_' + response.status,
          message: response.statusText || 'Request failed'
        },
        timestamp: new Date().toISOString()
      };
    }

    // Map HTTP status codes to appropriate error types
    switch (response.status) {
      case 400:
        throw new PaymentError(
          errorData.error.code,
          errorData.error.message,
          { ...errorData.error.details, requestId, endpoint }
        );
      
      case 401:
        // Clear invalid token
        this.clearAccessToken();
        throw new PaymentError(
          'AUTH_REQUIRED',
          'Authentication required. Please log in again.',
          { requestId, endpoint }
        );
      
      case 402:
        throw new PaymentError(
          errorData.error.code,
          errorData.error.message,
          { ...errorData.error.details, requestId, endpoint }
        );
      
      case 404:
        throw new ServiceError(
          errorData.error.code,
          errorData.error.message,
          { ...errorData.error.details, requestId, endpoint }
        );
      
      case 429:
        throw new PaymentError(
          'RATE_LIMITED',
          'Too many requests. Please wait a moment and try again.',
          { requestId, endpoint, retryAfter: response.headers.get('Retry-After') }
        );
      
      case 503:
        throw new ServiceError(
          'SERVICE_UNAVAILABLE',
          'Payment service is temporarily unavailable. Please try again later.',
          { requestId, endpoint }
        );
      
      default:
        throw new PaymentError(
          errorData.error.code || 'UNKNOWN_ERROR',
          errorData.error.message || 'An unexpected error occurred',
          { ...errorData.error.details, requestId, endpoint, status: response.status }
        );
    }
  }

  /**
   * Validate response structure
   */
  private validateResponseStructure(data: any, endpoint: string): void {
    // Check if response has expected structure
    if (endpoint.startsWith('/services') && data.services === undefined && data.success === undefined) {
      console.warn(`⚠️  Unexpected response structure from ${endpoint}`, data);
    }
  }

  /**
   * Update access token when user logs in
   */
  setAccessToken(token: string): void {
    this.accessToken = token;
    localStorage.setItem('stratiqx_access_token', token);
    console.log('🔑 Access token updated');
  }

  /**
   * Clear access token
   */
  clearAccessToken(): void {
    this.accessToken = null;
    localStorage.removeItem('stratiqx_access_token');
    console.log('🗑️ Access token cleared');
  }

  /**
   * Get available services (replaces getPlans)
   */
  async getServices(): Promise<{ success: boolean; services: ServiceDetails[]; count: number }> {
    try {
      const response = await this.request<{ success: boolean; services: ServiceDetails[]; count: number }>('/services');
      
      if (!response.success || !Array.isArray(response.services)) {
        throw new ServiceError(
          'INVALID_RESPONSE',
          'Invalid services response format',
          { response }
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch services:', error);
      throw ErrorHandler.wrapError(error, 'Failed to load available services');
    }
  }

  /**
   * Get user's active services (replaces getUserStatus)
   */
  async getUserServices(): Promise<UserServices> {
    try {
      const response = await this.request<UserServices>('/user-services');
      
      if (!response.success) {
        throw new ServiceError(
          'INVALID_RESPONSE',
          'Invalid user services response format',
          { response }
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch user services:', error);
      throw ErrorHandler.wrapError(error, 'Failed to load your services');
    }
  }

  /**
   * Create payment intent for service
   */
  async createPaymentIntent(paymentData: PaymentRequest): Promise<PaymentIntent> {
    try {
      // Validate input
      this.validatePaymentRequest(paymentData);

      const response = await this.request<PaymentIntent>('/create-payment-intent', {
        method: 'POST',
        body: JSON.stringify(paymentData),
      });

      if (!response.success || !response.clientSecret) {
        throw new PaymentError(
          'INVALID_RESPONSE',
          'Invalid payment intent response format',
          { response }
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to create payment intent:', error);
      throw ErrorHandler.wrapError(error, 'Failed to initialize payment');
    }
  }

  /**
   * Validate promo code
   */
  async validatePromoCode(promoCode: string, amount?: number): Promise<PromoValidation> {
    try {
      if (!promoCode || typeof promoCode !== 'string' || promoCode.trim().length === 0) {
        throw new PaymentError(
          'INVALID_PROMO_CODE',
          'Promo code is required',
          { promoCode }
        );
      }

      return this.request<PromoValidation>('/validate-promo', {
        method: 'POST',
        body: JSON.stringify({
          code: promoCode.trim().toUpperCase(),
          amount: amount || 0
        }),
      });
    } catch (error) {
      console.error('Failed to validate promo code:', error);
      throw ErrorHandler.wrapError(error, 'Failed to validate promo code');
    }
  }

  /**
   * Confirm payment through our service (instead of directly to Stripe)
   */
  async confirmPayment(paymentIntentId: string, paymentMethodId: string): Promise<{
    success: boolean;
    paymentIntentId: string;
    status: string;
    amount: number;
    serviceType: string;
    emails: {
      sent: boolean;
      details: any;
    };
  }> {
    try {
      if (!paymentIntentId || !paymentMethodId) {
        throw new PaymentError(
          'INVALID_PARAMETERS',
          'Payment intent ID and payment method ID are required',
          { paymentIntentId, paymentMethodId }
        );
      }

      const response = await this.request('/confirm-payment', {
        method: 'POST',
        body: JSON.stringify({ 
          paymentIntentId, 
          paymentMethodId 
        }),
      });

      if (!response.success) {
        throw new PaymentError(
          response.error?.code || 'PAYMENT_CONFIRMATION_FAILED',
          response.error?.message || 'Payment confirmation failed',
          response.error?.details
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to confirm payment:', error);
      throw ErrorHandler.wrapError(error, 'Failed to confirm payment');
    }
  }

  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; version: string; model: string }> {
    try {
      return this.request('/health');
    } catch (error) {
      console.error('Health check failed:', error);
      throw ErrorHandler.wrapError(error, 'Service health check failed');
    }
  }

  /**
   * Get service information by ID
   */
  getServiceInfo(serviceId: string): ServiceDetails | null {
    // Use centralized service configuration instead of hardcoded values
    const serviceConfig = ServiceConfigUtils.getService(serviceId);
    if (!serviceConfig || !serviceConfig.active) {
      return null;
    }

    return {
      id: serviceConfig.id,
      name: serviceConfig.name,
      description: serviceConfig.description,
      price: serviceConfig.price,
      deposit: serviceConfig.deposit,
      billingType: serviceConfig.billingType,
      features: serviceConfig.features,
      deliveryTime: serviceConfig.metadata.deliveryTime || 'TBD'
    };
  }

  /**
   * Get payment details for service (replaces getPaymentPlanForService)
   */
  getPaymentDetailsForService(serviceId: string): { amount: number; serviceInfo: ServiceDetails | null } {
    const serviceInfo = this.getServiceInfo(serviceId);
    
    // Use deposit amount if available, otherwise use full price
    // For free services (price.value === 0), return 0
    let amount = 0;
    if (serviceInfo) {
      if (serviceInfo.deposit?.amount > 0) {
        amount = serviceInfo.deposit.amount;
      } else if (serviceInfo.deposit?.type === 'none' && serviceInfo.price?.value > 0) {
        // No deposit required, use full price
        amount = serviceInfo.price.value;
      }
    }
    
    return { 
      amount,
      serviceInfo
    };
  }

  /**
   * Get all available services from configuration
   */
  getAvailableServices(): ServiceDetails[] {
    return ServiceConfigUtils.getActiveServices()
      .map(service => ServiceConfigUtils.toPaymentServiceFormat(service));
  }

  /**
   * Validate payment request
   */
  private validatePaymentRequest(paymentData: PaymentRequest): void {
    const errors: string[] = [];

    if (typeof paymentData.amount !== 'number' || paymentData.amount < 0) {
      errors.push('Valid amount is required');
    }

    if (!paymentData.serviceType || typeof paymentData.serviceType !== 'string') {
      errors.push('Service type is required');
    }

    if (!paymentData.userId || typeof paymentData.userId !== 'string') {
      errors.push('User ID is required');
    }

    // Note: No minimum amount validation here - promo codes can reduce price below minimum
    // The backend will validate against service price and apply promo code discount

    if (errors.length > 0) {
      throw new PaymentError(
        'VALIDATION_FAILED',
        'Payment request validation failed',
        { errors, paymentData }
      );
    }
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get request statistics
   */
  getRequestStats(): { activeRequests: number; totalRequests: number } {
    return {
      activeRequests: this.requestTracker.size,
      totalRequests: this.requestTracker.size // This would be cumulative in a real implementation
    };
  }
}

export const paymentService = new PaymentServiceV2();